import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
import ACL from '@components/ACL/ACL'

import { formatDate, sendMetric } from '@atheneum/api/dist/lib'

import Row from '@components/Details/Row'
import { StatusFilter } from '@components/StatusFilter'

class ProjectDetails extends Component {
  static propTypes = {
    staticData: PropTypes.object.isRequired,
    projectStore: PropTypes.object.isRequired,
    sidebarStore: PropTypes.object.isRequired
  }

  renderContractList = () => {
    const { applicableContracts } = this.props.projectStore

    if (applicableContracts.length) {
      return (
        <div className="row__list">
          {applicableContracts.map(contract => (
            <div
              key={contract.id}
              className="row--link"
              onClick={() =>
                this.props.sidebarStore.openSidebar({
                  template: 'contract',
                  id: contract.id
                })
              }>
              {contract.contractReference}
            </div>
          ))}
        </div>
      )
    }
  }

  renderAuditors = auditors => {
    return auditors.map((auditor, index) => {
      let label = ''
      if (index === 0) {
        label = 'Compliance auditors'
      }
      return <Row key={auditor.id} label={label} text={auditor.fullName} />
    })
  }

  render() {
    const { staticData } = this.props
    const { project, projectStartEndDate } = this.props.projectStore
    const {
      accountTypeId,
      hedgeFundRequired
    } = project.office.account.parentAccount
    const { externalComplianceRequired } = project.office.account
    const { complianceRequired } = project
    const accountTypeName = staticData.names.accountTypes[accountTypeId]
    const { ACCOUNT_TYPES } = staticData.CONST
    const KPMG_PARENT_ACCOUNT_ID = 97
    const feedbacks = toJS(
      this.props.projectStore.clientFeedbackStore.feedbacks
    ).filter(feedback => feedback.projectId === project.id)
    let projectCategory = staticData.projectCategories.find(c => c.id === project.projectCategoryId)
    projectCategory = projectCategory ? projectCategory.name : '-'

    // NOTE: disable sending/re-sending client feedback for bain and company
    const isClientFeedbackHidden = (project.parentAccountId === 14)

    return (
      <div className="story">
        <span className="label">Project details</span>
        <div className="group-wrapper">
          <div className="group">
            <Row label="Type" text={project.type} />
            <Row label="Project category" text={projectCategory} />
            <Row
              label="BD Manager contact"
              text={project.bdContact && project.bdContact.fullName}
            />
            <Row
              label="Start/end date"
              text={projectStartEndDate}
              tooltip="Project start date reflects when project was created
                       and end date reflects when it was closed"
            />
            <Row
              label="Client office"
              text={project.office.officeName}
              onClick={() =>
                this.props.sidebarStore.openSidebar({
                  template: 'client-office',
                  id: project.office.id
                })
              }
            />
            <Row
              label="Client account"
              text={project.office.account.companyName}
              onClick={() =>
                this.props.sidebarStore.openSidebar({
                  template: 'client-account',
                  id: project.office.account.id
                })
              }
            />
            <Row label="Parent account type" text={accountTypeName} />
            {accountTypeId === ACCOUNT_TYPES.HEDGE_FUND && (
              <Row label="HF compliance required">
                {hedgeFundRequired ? 'Yes' : 'No'}
              </Row>
            )}
            <Row label="Case code" text={project.caseCode} />
            <Row
              label="External Compliance Verification"
              text={(externalComplianceRequired && complianceRequired) ? 'Required' : 'Not required'}
            />
            {(externalComplianceRequired && complianceRequired) &&
              project.auditors?.length &&
              this.renderAuditors(project.auditors)}
            <Row label="Applicable contracts">{this.renderContractList()}</Row>
            <Row
              label="Industry and subindustry"
              text={`${project.industry} / ${project.subIndustry}`}
            />
            <Row
              label={
                KPMG_PARENT_ACCOUNT_ID === project.parentAccountId
                  ? 'EP' : 'EM'
              }
              text={project.engagementManager}
            />
            <Row
              label="EC"
              text={project.engagementCode}
            />
            {
              project.projectLeadName && <Row
                label="Project lead name"
                text={project.projectLeadName}
              />
            }
            {
              project.projectLeadEmail && <Row
                label="Project lead email"
                text={project.projectLeadEmail}
              />
            }
            {
              project.projectDirectorName && <Row
                label="Project director name"
                text={project.projectDirectorName}
              />
            }
            {
              project.projectDirectorEmail && <Row
                label="Project director email"
                text={project.projectDirectorEmail}
              />
            }
            {
              project.projectLocationName && <Row
                label="Project location name"
                text={project.projectLocationName}
              />
            }
            {
              project.projectLocationAddress && <Row
                label="Project location address"
                text={project.projectLocationAddress}
              />
            }
            {
              project.projectLocationCountry && <Row
                label="Project location country"
                text={project.projectLocationCountry}
              />
            }
            {
              project.capiProjectType && <Row
                label="Project Type"
                text={project.capiProjectType}
                boldText={project.capiProjectType === 'LOP'}
              />
            }
          </div>
          <div className="group">
            <div className="row">
              <span className="row__name">Project status</span>
              <StatusFilter
                single
                type="project"
                selectedItems={[
                  this.props.projectStore.project.projectStatusId
                ]}
                onChange={async val => {
                  await this.props.projectStore.changeStatus(val)
                }}
              />
            </div>
          </div>
          <Row
            label="Preliminary list / Proposal"
            text={project.inProposal ? 'Yes' : 'No'}
          />
          <Row
            label="Apply before date"
            text={formatDate(project.applyBeforeDate, 'MMM DD YYYY', '-')}
          />
          <Row
            label="Additional client agreements"
            text={project.additionalClientArangements}
          />
          {!isClientFeedbackHidden && (
            <ClientFeedback
              roles={['Admin', 'ProjectManagement']}
              feedbacks={feedbacks}
              onClick={(buttonText) => {
                this.props.projectStore.sendAdHocFeedback()
                sendMetric(`ProjectDetails.sendClientFeedback.${buttonText}`)
              }}
            />
          )}
        </div>
      </div>
    )
  }
}

const ClientFeedback = ({ roles, feedbacks, onClick }) => {
  const submittedFeedback = feedbacks.find(feedback => feedback.dateSubmitted)

  let status = ''
  let showStatus = false
  let buttonText = 'Send'

  if (feedbacks.length) {
    status = 'Sent'
    showStatus = true
    buttonText = 'Resend'
  }

  if (submittedFeedback) {
    status = 'Submitted'
    showStatus = true
  }

  return (
    <ACL roles={roles}>
      <div className="row client-feedback-status">
        <span className="row__name">Client feedback status:</span>
        <span className="row__value">
          {showStatus && <span className="status" style={{ marginRight: '14px' }}>{status}</span>}
          <span class="row--link" style={{ borderBottom: '1px solid #3A86BF' }} onClick={() => {onClick(buttonText)}}>{buttonText}</span>
        </span>
      </div>
    </ACL>
  )
}

export default inject(
  'staticData',
  'projectStore',
  'sidebarStore'
)(observer(ProjectDetails))
